import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { AccordionGroup } from '@sola/ark-core/molecules/accordion-group/accordion-group.tsx';
import { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { HouseholdMemberItem } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';
interface ChildDetailsOverviewListItemProps {
  title?: string;
  children: React.ReactNode;
}
const ChildDetailsOverviewListItem: React.FC<ChildDetailsOverviewListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsListProps {
  children: React.ReactNode;
}
const ChildDetailsList: React.FC<ChildDetailsListProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ul className="p-0 list-none m-0 flex flex-col gap-snug-sm">{children}</ul>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}
const ChildDetailsListItem: React.FC<ChildDetailsListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <ChildDetailsList>{children}</ChildDetailsList>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="flex flex-col body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsAccordionSectionProps {
  children: React.ReactNode;
}
const ChildDetailsAccordionSection: React.FC<ChildDetailsAccordionSectionProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="p-0 list-none m-0 flex flex-col gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const getPrimaryCareGiver = (householdMemberList: HouseholdMemberItem[]) => {
  return householdMemberList.find(member => member.isPrimaryCaregiver)?.householdMemberRole;
};
export const ChildBioAccordion = t0 => {
  const $ = _c(139);
  const {
    details,
    commitment
  } = t0;
  const {
    t
  } = useTranslation("my");
  const preferredName = details.name.preferred;
  let t1;
  if ($[0] !== t) {
    t1 = t("sponsorshipRelationship.childBio.overview.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("sponsorshipRelationship.childBio.overview.age");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== details.birthDate) {
    t3 = getAgeFromBirthday(details.birthDate);
    $[4] = details.birthDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <ChildDetailsOverviewListItem title={t2}>{t3}</ChildDetailsOverviewListItem>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("sponsorshipRelationship.childBio.overview.birthday");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== details.birthDate || $[12] !== t) {
    t6 = t("sponsorshipRelationship.childBio.overview.birthdayValue", {
      date: details.birthDate
    });
    $[11] = details.birthDate;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <ChildDetailsOverviewListItem title={t5}>{t6}</ChildDetailsOverviewListItem>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("sponsorshipRelationship.childBio.overview.gender");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== details.gender || $[20] !== t8) {
    t9 = <ChildDetailsOverviewListItem title={t8}>{details.gender}</ChildDetailsOverviewListItem>;
    $[19] = details.gender;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("sponsorshipRelationship.childBio.overview.location");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== details.country || $[25] !== t10) {
    t11 = <ChildDetailsOverviewListItem title={t10}>{details.country}</ChildDetailsOverviewListItem>;
    $[24] = details.country;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("sponsorshipRelationship.childBio.overview.childId");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== details.localId || $[30] !== t12) {
    t13 = <ChildDetailsOverviewListItem title={t12}>{details.localId}</ChildDetailsOverviewListItem>;
    $[29] = details.localId;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t13 || $[34] !== t4 || $[35] !== t7 || $[36] !== t9) {
    t14 = <ChildDetailsList>{t4}{t7}{t9}{t11}{t13}</ChildDetailsList>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t4;
    $[35] = t7;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== commitment.dates || $[39] !== preferredName || $[40] !== t) {
    t15 = commitment.dates?.start && <p className="body-sm label-neutral-mild">{t("sponsorshipRelationship.childBio.overview.sponsorshipLength", {
        childName: preferredName,
        date: commitment.dates.start
      })}</p>;
    $[38] = commitment.dates;
    $[39] = preferredName;
    $[40] = t;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t14 || $[43] !== t15) {
    t16 = <ChildDetailsAccordionSection>{t14}{t15}</ChildDetailsAccordionSection>;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t1 || $[46] !== t16) {
    t17 = {
      header: t1,
      children: t16
    };
    $[45] = t1;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t) {
    t18 = t("sponsorshipRelationship.childBio.education.title");
    $[48] = t;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== details.education.academicPerformance || $[51] !== t) {
    t19 = details.education.academicPerformance && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.performance")}>{details.education.academicPerformance}</ChildDetailsListItem>;
    $[50] = details.education.academicPerformance;
    $[51] = t;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== details.education.formalLevel || $[54] !== t) {
    t20 = details.education.formalLevel && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.educationLevel")}>{details.education.formalLevel}</ChildDetailsListItem>;
    $[53] = details.education.formalLevel;
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== details.education.gradeLevel.us || $[57] !== t) {
    t21 = details.education.gradeLevel.us && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.gradeLevel")}>{details.education.gradeLevel.us}</ChildDetailsListItem>;
    $[56] = details.education.gradeLevel.us;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== details.education.gradeLevel.local || $[60] !== t) {
    t22 = details.education.gradeLevel.local && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.localGradeName")}>{details.education.gradeLevel.local}</ChildDetailsListItem>;
    $[59] = details.education.gradeLevel.local;
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== details.interests.favoriteSchoolSubject || $[63] !== t) {
    t23 = details.interests.favoriteSchoolSubject.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.favoriteSubjects")}><ChildDetailsList>{details.interests.favoriteSchoolSubject.map(_temp)}</ChildDetailsList></ChildDetailsListItem>;
    $[62] = details.interests.favoriteSchoolSubject;
    $[63] = t;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== t) {
    t24 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelps");
    $[65] = t;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== details.gender || $[68] !== preferredName || $[69] !== t) {
    t25 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelpsSummary", {
      childName: preferredName,
      context: details.gender
    });
    $[67] = details.gender;
    $[68] = preferredName;
    $[69] = t;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t24 || $[72] !== t25) {
    t26 = <ChildDetailsListItem title={t24}>{t25}</ChildDetailsListItem>;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t19 || $[75] !== t20 || $[76] !== t21 || $[77] !== t22 || $[78] !== t23 || $[79] !== t26) {
    t27 = <ChildDetailsAccordionSection>{t19}{t20}{t21}{t22}{t23}{t26}</ChildDetailsAccordionSection>;
    $[74] = t19;
    $[75] = t20;
    $[76] = t21;
    $[77] = t22;
    $[78] = t23;
    $[79] = t26;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t18 || $[82] !== t27) {
    t28 = {
      header: t18,
      children: t27
    };
    $[81] = t18;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t) {
    t29 = t("sponsorshipRelationship.childBio.family.title");
    $[84] = t;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== details.householdList || $[87] !== t) {
    t30 = details.householdList?.[0]?.householdMemberList && details.householdList[0].householdMemberList.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.primaryCaregiver")}>{getPrimaryCareGiver(details.householdList[0].householdMemberList)}</ChildDetailsListItem>;
    $[86] = details.householdList;
    $[87] = t;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  let t31;
  if ($[89] !== details.householdDuty || $[90] !== t) {
    t31 = details.householdDuty.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.duties")}><ChildDetailsList>{details.householdDuty.map(_temp2)}</ChildDetailsList></ChildDetailsListItem>;
    $[89] = details.householdDuty;
    $[90] = t;
    $[91] = t31;
  } else {
    t31 = $[91];
  }
  let t32;
  if ($[92] !== t) {
    t32 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelps");
    $[92] = t;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t) {
    t33 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelpsParagraph");
    $[94] = t;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== t32 || $[97] !== t33) {
    t34 = <ChildDetailsListItem title={t32}>{t33}</ChildDetailsListItem>;
    $[96] = t32;
    $[97] = t33;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  let t35;
  if ($[99] !== t30 || $[100] !== t31 || $[101] !== t34) {
    t35 = <ChildDetailsAccordionSection>{t30}{t31}{t34}</ChildDetailsAccordionSection>;
    $[99] = t30;
    $[100] = t31;
    $[101] = t34;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== t29 || $[104] !== t35) {
    t36 = {
      header: t29,
      children: t35
    };
    $[103] = t29;
    $[104] = t35;
    $[105] = t36;
  } else {
    t36 = $[105];
  }
  let t37;
  if ($[106] !== t) {
    t37 = t("sponsorshipRelationship.childBio.activities.title");
    $[106] = t;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== details.interests.christianActivityName || $[109] !== t) {
    t38 = details.interests.christianActivityName.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.christian")}><ChildDetailsList>{details.interests.christianActivityName.map(_temp3)}</ChildDetailsList></ChildDetailsListItem>;
    $[108] = details.interests.christianActivityName;
    $[109] = t;
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  let t39;
  if ($[111] !== details.interests.favoriteProjectActivity || $[112] !== t) {
    t39 = details.interests.favoriteProjectActivity.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.project")}><ChildDetailsList>{details.interests.favoriteProjectActivity.map(_temp4)}</ChildDetailsList></ChildDetailsListItem>;
    $[111] = details.interests.favoriteProjectActivity;
    $[112] = t;
    $[113] = t39;
  } else {
    t39 = $[113];
  }
  let t40;
  if ($[114] !== details.interests.thingsILike || $[115] !== preferredName || $[116] !== t) {
    t40 = details.interests.thingsILike.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.thingsChildLikes", {
      childName: preferredName
    })}><ChildDetailsList>{details.interests.thingsILike.map(_temp5)}</ChildDetailsList></ChildDetailsListItem>;
    $[114] = details.interests.thingsILike;
    $[115] = preferredName;
    $[116] = t;
    $[117] = t40;
  } else {
    t40 = $[117];
  }
  let t41;
  if ($[118] !== t) {
    t41 = t("sponsorshipRelationship.childBio.activities.howYourSponsorshipHelps");
    $[118] = t;
    $[119] = t41;
  } else {
    t41 = $[119];
  }
  let t42;
  if ($[120] !== preferredName || $[121] !== t) {
    t42 = t("sponsorshipRelationship.childBio.activities.howYourSponsorshipHelpsParagraph", {
      childName: preferredName
    });
    $[120] = preferredName;
    $[121] = t;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  let t43;
  if ($[123] !== t41 || $[124] !== t42) {
    t43 = <ChildDetailsListItem title={t41}>{t42}</ChildDetailsListItem>;
    $[123] = t41;
    $[124] = t42;
    $[125] = t43;
  } else {
    t43 = $[125];
  }
  let t44;
  if ($[126] !== t38 || $[127] !== t39 || $[128] !== t40 || $[129] !== t43) {
    t44 = <ChildDetailsAccordionSection>{t38}{t39}{t40}{t43}</ChildDetailsAccordionSection>;
    $[126] = t38;
    $[127] = t39;
    $[128] = t40;
    $[129] = t43;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  let t45;
  if ($[131] !== t37 || $[132] !== t44) {
    t45 = {
      header: t37,
      children: t44
    };
    $[131] = t37;
    $[132] = t44;
    $[133] = t45;
  } else {
    t45 = $[133];
  }
  let t46;
  if ($[134] !== t17 || $[135] !== t28 || $[136] !== t36 || $[137] !== t45) {
    const accordionItems = [t17, t28, t36, t45];
    t46 = <AccordionGroup items={accordionItems} />;
    $[134] = t17;
    $[135] = t28;
    $[136] = t36;
    $[137] = t45;
    $[138] = t46;
  } else {
    t46 = $[138];
  }
  return t46;
};
function _temp(subject) {
  return <ChildDetailsOverviewListItem key={`favorite-school-subject-${subject}`}>{subject}</ChildDetailsOverviewListItem>;
}
function _temp2(duty) {
  return <ChildDetailsOverviewListItem key={`household-duty-${duty}`}>{duty}</ChildDetailsOverviewListItem>;
}
function _temp3(activity) {
  return <ChildDetailsOverviewListItem key={`christian-activity-${activity}`}>{activity}</ChildDetailsOverviewListItem>;
}
function _temp4(activity_0) {
  return <ChildDetailsOverviewListItem key={`favorite-project-activity-${activity_0}`}>{activity_0}</ChildDetailsOverviewListItem>;
}
function _temp5(activity_1) {
  return <ChildDetailsOverviewListItem key={`things-i-like-${activity_1}`}>{activity_1}</ChildDetailsOverviewListItem>;
}